import PageTitle from "@/components/page-title/page-title.vue";
import { PermissionManagerLocale } from "@/locales/localeid";
import { ContentAdmin } from "@/models/user-model";
import User from "@/modules/user";
import { AccessLevel, PermissionOrder, VcsMenuCategory, VcsMenuCategoryPermission } from "@/util/enum";
import { isLinkVisible } from "@/util/func";
import { Paths } from "@/util/paths";
import { Button, Checkbox, Radio, Select, Spin } from "ant-design-vue";
import {
	AllowedPermissionRequestModal,
	fmtMsg,
	MessageHelper,
	NotificationType,
	Permission,
	PermissionService
} from "commonui";
import { isNull, orderBy } from "lodash";
import { Component, Vue } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

@Component({
	components: {
		PageTitle,
		Checkbox,
		Radio,
		RadioGroup: Radio.Group,
		Button,
		Spin,
		Select,
		Option: Select.Option
	}
})
export default class PermissionManager extends Vue {
	public permissions: Permission[] = [];
	public userModule = getModule(User) as User;
	public loading = false;
	public selectedUser: string | null = null;
	public contentAdmins: ContentAdmin[] = [];
	public accessLevel = AccessLevel;
	public isPermissionChanged = false;

	created() {
		const { selectedAdmin, getContentAdmins, setSelectedAdmin } = this.userModule;
		this.contentAdmins = getContentAdmins.filter((admin) => !admin.isInvitePending);
		if (selectedAdmin.length) {
			this.selectedUser = selectedAdmin;
			this.getPermissionById(this.selectedUser);
			setSelectedAdmin("");
		} else if (this.contentAdmins.length > 0) {
			this.selectedUser = this.contentAdmins[0].id;
			this.getPermissionById(this.selectedUser);
		}
	}

	get applicationPaths() {
		return Paths;
	}

	get vcsMenuCategory() {
		return VcsMenuCategory;
	}

	get vcsMenuPermissionCategory() {
		return VcsMenuCategoryPermission;
	}

	get permissionManagerLocales() {
		return PermissionManagerLocale;
	}

	get curriculumPermWithoutConversationTemplates() {
		const permission = this.permissions.find((f) => f.id === VcsMenuCategory.ListManagement);
		if (permission && permission.items) {
			return permission.items.filter((f) => f.id != VcsMenuCategory.ConversationTemplates);
		}
		return [];
	}
	get curriculumPermWithoutCompPerm() {
		const permission = this.permissions.find((f) => f.id === VcsMenuCategory.CurriculumManagement);
		if (permission && permission.items) {
			return permission.items.filter((f) => (f.items ? f.id != VcsMenuCategory.Components && f.id != VcsMenuCategory.NonCurriculumComponents : true));
		}
		return [];
	}

	get isSpinning() {
		return this.userModule.isLoading || this.loading;
	}

	get isNoPermission() {
		return this.permissions.length === 0 ? true : false;
	}

	showLink(menuId: VcsMenuCategory, permissionId?: VcsMenuCategoryPermission, superMenuId?: VcsMenuCategory) {
		return isLinkVisible(menuId, permissionId, superMenuId);
	}

	localizedString(id: string, params?: {}) {
		return fmtMsg(id, params);
	}

	onUserChange(value) {
		this.getPermissionById(value);
		this.isPermissionChanged = false;
	}

	onRadioChanged() {
		if (!this.isPermissionChanged) {
			this.isPermissionChanged = true;
		}
	}

	formatPermissions(permissions) {
		const formattedPermissions = permissions.map((permission) => {
			if (permission.id === this.vcsMenuCategory.UserManagement) {
				permission.order = PermissionOrder.UserManagement;
			} else if (permission.id === this.vcsMenuCategory.AuditHistory) {
				permission.order = PermissionOrder.AuditHistory;
				const componentItem = permission.items;
				if (componentItem && componentItem.length) {
					componentItem.items = componentItem.map((item) => {
						if (item.id === this.vcsMenuPermissionCategory.AuditHistory) {
							item.order = 1;
						}
						return item;
					});
				}
				componentItem.sort((a, b) => {
					return a.order - b.order;
				});
				permission.items = componentItem;
			} else if (permission.id === this.vcsMenuCategory.ListManagement) {
				permission.order = PermissionOrder.ListManagement;
				const componentItem = permission.items.map(x => x);

				if (componentItem && componentItem.length) {
					componentItem.items = componentItem.map((item) => {
						if (item.id === this.vcsMenuPermissionCategory.UnitList) {
							item.order = 2;
						}
						else if (item.id === this.vcsMenuPermissionCategory.CategoryManager) {
							item.order = 3;
						}
						else if (item.id === this.vcsMenuPermissionCategory.ObjectiveManager) {
							item.order = 4;
						}
						else if (item.id === this.vcsMenuCategory.ConversationTemplates) {
							item.order = 1;
						}
						else if (item.id === this.vcsMenuPermissionCategory.Conversations) {
							item.order = 5;
						}
						else {
							item.order = 6;
						}

						return item;
					});
					componentItem.sort((a, b) => {
						return a.order - b.order;
					});
					permission.items = componentItem;
				}

			} else if (permission.id === this.vcsMenuCategory.ActiveLearn) {
				permission.order = PermissionOrder.ActiveLearn;
				const componentItem = permission.items.map(x => x);
				if (componentItem && componentItem.length) {
					componentItem.items = componentItem.map((item) => {
						if (item.id === this.vcsMenuPermissionCategory.AssetList) {
							item.order = 1;
						}
						else if (item.id === this.vcsMenuPermissionCategory.StudentActivities) {
							item.order = 2;
						}
						return item;
					});
				}
				componentItem.sort((a, b) => {
					return a.order - b.order;
				});
				permission.items = componentItem;
			}
			else if (permission.id === this.vcsMenuCategory.CurriculumManagement) {
				permission.order = PermissionOrder.CurriculumManagement;
				const componentItem = permission.items.find((item) => item.id === this.vcsMenuCategory.Components);
				if (componentItem.items && componentItem.items.length) {
					componentItem.items = orderBy(componentItem.items.map((item) => {
						if (item.id === this.vcsMenuPermissionCategory.Text) {
							item.order = 1;
						} else if (item.id === this.vcsMenuPermissionCategory.ManageChapters) {
							item.order = 2;
						} else if (item.id === this.vcsMenuPermissionCategory.Objectives) {
							item.order = 3;
						} else if (item.id === this.vcsMenuPermissionCategory.TeachingPlan) {
							item.order = 4;
						} else if (item.id === this.vcsMenuPermissionCategory.REP) {
							item.order = 5;
						} else if (item.id === this.vcsMenuPermissionCategory.Conversations) {
							item.order = 6;
						}
						return item;
					}),
						["order"],
						["asc"]
					);
				}
			} else if (permission.id === this.vcsMenuCategory.LessonPlan) {
				permission.order = PermissionOrder.LessonPlan;
				if (permission.items && permission.items.length) {
					permission.items = orderBy(permission.items.map((item) => {
						if (item.id === this.vcsMenuPermissionCategory.LessonPlanVersion) {
							item.order = 1;
						} else if (item.id === this.vcsMenuPermissionCategory.LessonPlanList) {
							item.order = 2;
						} else if (item.id === this.vcsMenuPermissionCategory.ObjectiveManager) {
							item.order = 3;
						} else if (item.id === this.vcsMenuPermissionCategory.CommonEntities) {
							item.order = 4;
						} else if (item.id === this.vcsMenuPermissionCategory.Reports) {
							item.order = 5;
						} else if (item.id === this.vcsMenuPermissionCategory.LessonPlanTemplate) {
							item.order = 6;
						}
						return item;
					}),
						["order"],
						["asc"]
					);
				}
			} else if (permission.id === this.vcsMenuCategory.LanguageFunctionManagement) {
				permission.order = PermissionOrder.LanguageFunctionManagement;
			} else if (permission.id === this.vcsMenuCategory.Vocabulary) {
				permission.order = PermissionOrder.Vocabulary;
				if (permission.items && permission.items.length) {
					permission.items = orderBy(permission.items.map((item) => {
						if (item.id === this.vcsMenuPermissionCategory.WordPhraseSearch) {
							item.order = 1;
						} else if (item.id === this.vcsMenuPermissionCategory.WordCount) {
							item.order = 2;
						} else {
							item.order = 3;
						}
						return item;
					}),
						["order"],
						["asc"]
					);
				}
			} else if (permission.id === this.vcsMenuCategory.TextAnalysis) {
				permission.order = PermissionOrder.TextAnalysis;
				if (permission.items && permission.items.length) {
					permission.items = orderBy(permission.items.map((item) => {
						if (item.id === this.vcsMenuPermissionCategory.ReaderWriterAnalysis) {
							item.order = 1;
						} else if (item.id === this.vcsMenuPermissionCategory.TextChangeAnalysis) {
							item.order = 2;
						} else {
							item.order = 3;
						}
						return item;
					}),
						["order"],
						["asc"]
					);
				}
			} else if (permission.id === this.vcsMenuCategory.Templates) {
				permission.order = PermissionOrder.Templates;
			}

			return permission;
		});
		return orderBy(formattedPermissions, ["order"], ["asc"]);
	}

	getPermissionById(userId: string) {
		this.loading = true;
		PermissionService.getPermissions(userId)
			.then((permissions: any) => {
				this.permissions = this.formatPermissions(permissions);
				this.loading = false;
			})
			.catch((error) => {
				this.loading = false;
			});
	}

	// onMenuToggle(e: any, menuId: VcsMenuCategory, subMenuId: VcsMenuCategory) {
	// 	const checkedVal = e.target.checked;
	// 	const list = this.permissions.find((per) => per.id === menuId)?.items?.find((item) => item.id === subMenuId);
	// 	if (!isUndefined(list)) {
	// 		list.items?.forEach((item) => {
	// 			item.isVisible = checkedVal;
	// 		});
	// 	}
	// }

	// onSubMenuToggle(e: any, menuId: VcsMenuCategory, subMenuId: VcsMenuCategory) {
	// 	const list = this.permissions.find((per) => per.id === menuId)?.items?.find((item) => item.id === subMenuId);
	// 	const listItems = list?.items;
	// 	if (!isUndefined(list) && !isUndefined(listItems)) {
	// 		const isAllVisible = listItems?.every((item) => item.isVisible);
	// 		if (!isUndefined(isAllVisible)) {
	// 			list.isVisible = isAllVisible;
	// 		}
	// 	}
	// }

	getMasterTemplatePermissions(items: Permission[]) {
		console.log("Test", items);
		return items.filter((f) => f.id);
	}

	getComponentPermissions(items: Permission[]) {
		return items.filter((i) => i.id === VcsMenuCategoryPermission.Components);
	}
	getConversationsPermissions(items: Permission[]) {
		return items.filter((i) => i.id === VcsMenuCategory.ConversationTemplates);
	}

	saveUserPermission() {
		if (isNull(this.selectedUser)) {
			MessageHelper.Message(
				NotificationType.Warning,
				this.localizedString(this.permissionManagerLocales.PermissionManagerNoUserErrorMsg)
			);
		}

		if (this.isNoPermission) {
			MessageHelper.Message(
				NotificationType.Warning,
				this.localizedString(this.permissionManagerLocales.PermissionManagerNoPermissionErrorMsg)
			);
		}

		const contentAdmin = this.contentAdmins.find(a => a.id === this.selectedUser);

		this.loading = true;
		PermissionService.saveUserPermissions(this.selectedUser, this.getCheckedPermissions(), contentAdmin)
			.then((data: any) => {
				this.loading = false;
				MessageHelper.Message(
					NotificationType.Success,
					this.localizedString(this.permissionManagerLocales.PermissionManagerSaveSuccessMsg)
				);
				this.isPermissionChanged = false;
			})
			.catch((error) => {
				this.loading = false;
			});
	}

	onTemplateChildChange(e, menuPermission: VcsMenuCategoryPermission) {
		this.onRadioChanged();
		const template = this.permissions.find((permission) => permission.id === VcsMenuCategory.Templates);

		if (template && template.items) {
			const masterTemplate = template.items.find((item) => item.id === VcsMenuCategory.ConversationTemplates);

			if (masterTemplate) {
				if (!masterTemplate.isVisible && e.target.value !== AccessLevel.None) {
					masterTemplate.isVisible = true;
					masterTemplate.accessLevel = AccessLevel.Full;
				}

				if (masterTemplate.items) {
					const menuItem = masterTemplate.items.find((item) => item.id === menuPermission);

					if (menuItem) {
						menuItem.isVisible = e.target.value !== AccessLevel.None;
						menuItem.accessLevel = e.target.value;
					}

					if (masterTemplate.items.every((item) => !item.isVisible)) {
						masterTemplate.isVisible = false;
						masterTemplate.accessLevel = AccessLevel.None;
					}
				}
			}
		}
	}

	onComponentsChange(e) {
		this.onRadioChanged();
		const template = this.permissions.find((permission) => permission.id === VcsMenuCategory.CurriculumManagement);

		if (template && template.items) {
			const components = template.items.find((item) => item.id === VcsMenuCategory.Components);
			const componentsPermission = template.items.find(
				(item) => item.id === VcsMenuCategoryPermission.Components
			);

			if (components && componentsPermission) {
				const perviousComponentPermission = components.accessLevel;

				components.isVisible = e.target.value !== AccessLevel.None;
				components.accessLevel = e.target.value;

				componentsPermission.isVisible = components.isVisible;
				componentsPermission.accessLevel = e.target.value;

				if (components.items) {
					components.items.forEach((item, index) => {
						// If none then move to null
						if (components.accessLevel === AccessLevel.None) {
							item.accessLevel = AccessLevel.None;
							item.isVisible = components.isVisible;
						} else if (
							components.accessLevel === AccessLevel.Full &&
							perviousComponentPermission === AccessLevel.None
						) {
							item.accessLevel = AccessLevel.Full;
							item.isVisible = components.isVisible;
						}
						// For other cases keep the granular permission
					});
				}
			}
		}
	}

	onComponentsChildChange(e, menuPermission: VcsMenuCategoryPermission) {
		this.onRadioChanged();
		const template = this.permissions.find((permission) => permission.id === VcsMenuCategory.CurriculumManagement);

		if (template && template.items) {
			const componnetsTemplate = template.items.find((item) => item.id === VcsMenuCategory.Components);
			const componnetsPermission = template.items.find(
				(item) => item.id === VcsMenuCategoryPermission.Components
			);

			if (componnetsTemplate && componnetsPermission) {
				if (!componnetsTemplate.isVisible && e.target.value !== AccessLevel.None) {
					componnetsTemplate.isVisible = true;
					componnetsTemplate.accessLevel = AccessLevel.Full;
					componnetsPermission.isVisible = true;
					componnetsPermission.accessLevel = AccessLevel.Full;
				}

				if (componnetsTemplate.items) {
					const menuItem = componnetsTemplate.items.find((item) => item.id === menuPermission);

					if (menuItem) {
						menuItem.isVisible = e.target.value !== AccessLevel.None;
						menuItem.accessLevel = e.target.value;
					}

					if (componnetsTemplate.items.every((item) => !item.isVisible)) {
						componnetsTemplate.isVisible = false;
						componnetsTemplate.accessLevel = AccessLevel.None;
						componnetsPermission.isVisible = false;
						componnetsPermission.accessLevel = AccessLevel.None;
					}
				}
			}
		}
	}

	get getComponentsChildItems() {
		const template = this.permissions.find((permission) => permission.id === VcsMenuCategory.CurriculumManagement);
		if (template && template.items) {
			const componnetsTemplate = template.items.find((item) => item.id === VcsMenuCategory.Components);
			return componnetsTemplate ? componnetsTemplate.items : null;
		}
		return null;
	}

	get getMasterTemplateChildItems() {
		const template = this.permissions.find((permission) => permission.id === VcsMenuCategory.ListManagement);
		if (template && template.items) {
			const componnetsTemplate = template.items
				.find((item) => item.id === VcsMenuCategory.ConversationTemplates)
				?.items?.filter((x) => x.id != VcsMenuCategory.MasterTemplate);
			return componnetsTemplate ? componnetsTemplate : null;
		}

		return null;
	}

	private getCheckedPermissions() {
		const ConversationTemplates = [
			VcsMenuCategoryPermission.Fallback,
			VcsMenuCategoryPermission.OpenClose,
			VcsMenuCategoryPermission.UtteranceEntities,
			VcsMenuCategoryPermission.CommonIntents
		];
		const checkedPermissions: AllowedPermissionRequestModal[] = [];
		this.permissions.forEach((permission) => {
			permission.items?.forEach((item) => {
				if (isNull(item.items) && item.accessLevel !== AccessLevel.None) {
					checkedPermissions.push({ permissionId: item.id, accessLevel: item.accessLevel });
				} else if (item.items) {
					item.items.forEach((subItem) => {
						if (subItem.accessLevel !== AccessLevel.None) {
							checkedPermissions.push({ permissionId: subItem.id, accessLevel: subItem.accessLevel });
						}
					});
				}
			});
		});

		const IsConversationPermissionExists = checkedPermissions.some((item) =>
			ConversationTemplates.includes(item.permissionId)
		);
		const conversationTemplatesIndex = checkedPermissions.findIndex(
			(el) => el.permissionId === VcsMenuCategoryPermission.ConversationTemplates
		);

		if (conversationTemplatesIndex === -1 && IsConversationPermissionExists) {
			checkedPermissions.push({ permissionId: VcsMenuCategoryPermission.ConversationTemplates, accessLevel: 1 });
		}

		if (!IsConversationPermissionExists && conversationTemplatesIndex > 0) {
			checkedPermissions.splice(conversationTemplatesIndex, 1);
		}
		return checkedPermissions;
	}
}
